// Weapon heat model (pure, Phaser-free; tests/combat-heat.test.ts).
//
// Heat is a 0..1 fraction of capacity:
//   - every shot (or burst / beam tick) adds heatPerShot × heatMult / stats.heatCapacity;
//     charge weapons add that amount per second while charging;
//   - cooling starts coolDelay after the last heat-adding action, at coolRate × coolMult × stats.cooling /s;
//   - reaching 1 overheats the weapon for overheatTime; heat drains linearly to 0 over that lockout;
//   - holstered weapons cool 1.5× faster with no delay (the lockout also runs 1.5× faster).
// Functions return event bit flags so the caller can play sounds / emit bus events without allocation.

export const HEAT_WARNING = 0.8;
/** Heat must fall below this before the warning can sound again (hysteresis). */
export const HEAT_WARNING_REARM = 0.65;
export const HOLSTER_COOL_MULT = 1.5;

/** Event flags returned by addHeat / tickHeat / ventHeat. */
export const HEAT_EV_OVERHEAT = 1;
export const HEAT_EV_WARNING = 2;
export const HEAT_EV_VENT_END = 4;

export interface HeatState {
  /** 0..1 fraction of capacity. */
  heat: number;
  /** Seconds since the last heat-adding action. */
  sinceShot: number;
  overheated: boolean;
  /** Seconds left in the overheat lockout. */
  overheatRemaining: number;
  /** Length of the current lockout (for the linear drain). */
  overheatTotal: number;
  /** The 0.8 warning has sounded for the current climb. */
  warned: boolean;
}

export function createHeatState(): HeatState {
  return { heat: 0, sinceShot: 999, overheated: false, overheatRemaining: 0, overheatTotal: 1, warned: false };
}

/** Heat one shot adds as a fraction of capacity. */
export function shotHeat(heatPerShot: number, heatMult: number, heatCapacity: number): number {
  return (Math.max(0, heatPerShot) * Math.max(0, heatMult)) / Math.max(0.05, heatCapacity);
}

/** Fraction of capacity cooled per second. */
export function coolingRate(coolRate: number, coolMult: number, cooling: number): number {
  return Math.max(0, coolRate) * Math.max(0, coolMult) * Math.max(0, cooling);
}

/**
 * Add heat (a shot, a beam tick, or charge heat for this frame). While the weapon is
 * overheated (lockout, or the MELTDOWN window) no heat is added: the bar keeps draining.
 */
export function addHeat(s: HeatState, amount: number, overheatTime: number): number {
  s.sinceShot = 0;
  if (s.overheated || !(amount > 0)) return 0;
  const before = s.heat;
  s.heat = before + amount;
  let ev = 0;
  if (!s.warned && before < HEAT_WARNING && s.heat >= HEAT_WARNING) {
    s.warned = true;
    ev |= HEAT_EV_WARNING;
  }
  if (s.heat >= 1) {
    s.heat = 1;
    s.overheated = true;
    s.overheatTotal = Math.max(0.05, overheatTime);
    s.overheatRemaining = s.overheatTotal;
    ev |= HEAT_EV_OVERHEAT;
  }
  return ev;
}

/** Advance cooling / the overheat lockout by dt seconds. */
export function tickHeat(s: HeatState, dt: number, coolPerSec: number, coolDelay: number, holstered: boolean): number {
  if (dt <= 0) return 0;
  if (s.overheated) {
    s.overheatRemaining -= dt * (holstered ? HOLSTER_COOL_MULT : 1);
    if (s.overheatRemaining <= 0) {
      s.overheated = false;
      s.overheatRemaining = 0;
      s.heat = 0;
      s.warned = false;
      s.sinceShot = 999;
      return HEAT_EV_VENT_END;
    }
    s.heat = s.overheatRemaining / s.overheatTotal;
    return 0;
  }
  const prevSince = s.sinceShot;
  s.sinceShot += dt;
  if (s.heat > 0) {
    let coolTime: number;
    if (holstered) coolTime = dt;
    else coolTime = Math.max(0, Math.min(dt, s.sinceShot - Math.max(prevSince, coolDelay)));
    const rate = coolPerSec * (holstered ? HOLSTER_COOL_MULT : 1);
    s.heat = Math.max(0, s.heat - rate * coolTime);
  }
  if (s.warned && s.heat < HEAT_WARNING_REARM) s.warned = false;
  return 0;
}

/** Instantly remove heat. During a lockout this shortens it proportionally (and can end it). */
export function ventHeat(s: HeatState, amount: number): number {
  if (!(amount > 0)) return 0;
  s.heat = Math.max(0, s.heat - amount);
  if (s.overheated) {
    s.overheatRemaining = s.heat * s.overheatTotal;
    if (s.heat <= 0) {
      s.overheated = false;
      s.overheatRemaining = 0;
      s.warned = false;
      s.sinceShot = 999;
      return HEAT_EV_VENT_END;
    }
  }
  if (s.warned && s.heat < HEAT_WARNING_REARM) s.warned = false;
  return 0;
}
